import React, { useEffect, useState } from 'react';
import { MapPin, Phone, User, Pencil, Plus, Trash2, X, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import communityRegistryApi, {
  HouseholdDetailResponse,
  MemberOut,
  MemberCreatePayload,
} from '../../services/communityRegistryApi';

interface MemberDraft {
  id?: number;
  full_name: string;
  sex: 'male' | 'female' | 'unknown';
  age_years: string;
  id_number: string;
  role_in_hh: string;
  is_pregnant: boolean;
  is_lactating: boolean;
  attending_anc: boolean;
  next_anc_date: string;
  nutrition_at_risk: boolean;
  muac_cm: string;
  has_danger_signs: boolean;
  suspected_illness: boolean;
  confirmed_illness: boolean;
  illness_type: string;
  death_reported: boolean;
  died_at: string;
}

function toDraft(m: MemberOut): MemberDraft {
  return {
    id: m.id,
    full_name: m.full_name,
    sex: (m.sex as 'male' | 'female') || 'unknown',
    age_years: m.age_years != null ? String(m.age_years) : '',
    id_number: m.id_number || '',
    role_in_hh: m.role_in_hh,
    is_pregnant: m.is_pregnant,
    is_lactating: m.is_lactating,
    attending_anc: m.attending_anc,
    next_anc_date: m.next_anc_date || '',
    nutrition_at_risk: m.nutrition_at_risk,
    muac_cm: m.muac_cm != null ? String(m.muac_cm) : '',
    has_danger_signs: m.has_danger_signs,
    suspected_illness: m.suspected_illness,
    confirmed_illness: m.confirmed_illness,
    illness_type: m.illness_type || '',
    death_reported: m.death_reported,
    died_at: m.died_at || '',
  };
}

function emptyDraft(): MemberDraft {
  return {
    full_name: '',
    sex: 'unknown',
    age_years: '',
    id_number: '',
    role_in_hh: 'member',
    is_pregnant: false,
    is_lactating: false,
    attending_anc: false,
    next_anc_date: '',
    nutrition_at_risk: false,
    muac_cm: '',
    has_danger_signs: false,
    suspected_illness: false,
    confirmed_illness: false,
    illness_type: '',
    death_reported: false,
    died_at: '',
  };
}

function draftToPayload(d: MemberDraft): MemberCreatePayload {
  return {
    full_name: d.full_name.trim(),
    sex: (d.sex === 'female' ? 'female' : 'male') as 'male' | 'female',
    age_years: Number(d.age_years) || 0,
    id_number: d.id_number.trim() || undefined,
    role_in_hh: d.role_in_hh === 'head' ? 'head' : 'member',
    is_pregnant: d.is_pregnant,
    is_lactating: d.is_lactating,
    attending_anc: d.attending_anc,
    next_anc_date: d.next_anc_date || undefined,
    nutrition_at_risk: d.nutrition_at_risk,
    muac_cm: d.muac_cm ? Number(d.muac_cm) : undefined,
    has_danger_signs: d.has_danger_signs,
    suspected_illness: d.suspected_illness,
    confirmed_illness: d.confirmed_illness,
    illness_type: d.illness_type.trim() || undefined,
    death_reported: d.death_reported,
    died_at: d.died_at || undefined,
  };
}

interface Props {
  householdId: number;
  onClose: () => void;
  onUpdated?: () => void;
}

const HouseholdDetailModal: React.FC<Props> = ({ householdId, onClose, onUpdated }) => {
  const [data, setData] = useState<HouseholdDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editMode, setEditMode] = useState(false);
  const [locality, setLocality] = useState('');
  const [villageNeighborhood, setVillageNeighborhood] = useState('');
  const [siteName, setSiteName] = useState('');
  const [addressText, setAddressText] = useState('');
  const [membersDraft, setMembersDraft] = useState<MemberDraft[]>([]);
  const [deletedMemberIds, setDeletedMemberIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  const load = () => {
    setLoading(true);
    communityRegistryApi
      .getHousehold(householdId)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load household'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId]);

  const enterEditMode = () => {
    if (!data) return;
    setLocality(data.locality || '');
    setVillageNeighborhood(data.village_neighborhood || '');
    setSiteName(data.site_name || '');
    setAddressText(data.address_text || '');
    setMembersDraft(data.members.map(toDraft));
    setDeletedMemberIds([]);
    setSaveError(null);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setSaveError(null);
  };

  const updateMemberDraft = (idx: number, patch: Partial<MemberDraft>) =>
    setMembersDraft((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeMemberDraft = (idx: number) => {
    const row = membersDraft[idx];
    if (row.id) setDeletedMemberIds((ids) => [...ids, row.id!]);
    setMembersDraft((rows) => rows.filter((_, i) => i !== idx));
  };

  const addMemberDraft = () => setMembersDraft((rows) => [...rows, emptyDraft()]);

  const membersValid = membersDraft.every((m) => m.full_name.trim() && m.age_years);

  const handleSave = async () => {
    if (!data || !membersValid) return;
    setSaving(true);
    setSaveError(null);
    try {
      await communityRegistryApi.updateHousehold(data.id, {
        locality: locality || undefined,
        village_neighborhood: villageNeighborhood || undefined,
        site_name: siteName || undefined,
        address_text: addressText || undefined,
      });

      for (const m of membersDraft) {
        const payload = draftToPayload(m);
        if (m.id) {
          await communityRegistryApi.updateMember(m.id, payload);
        } else {
          await communityRegistryApi.addMember(data.id, payload);
        }
      }
      for (const id of deletedMemberIds) {
        await communityRegistryApi.deleteMember(id);
      }

      load();
      setEditMode(false);
      onUpdated?.();
    } catch (e: any) {
      setSaveError(e?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!data || !noteText.trim()) return;
    setAddingNote(true);
    try {
      await communityRegistryApi.addNote(data.id, noteText.trim());
      setNoteText('');
      load();
    } catch {
      // surfaced via reload failing silently is acceptable here; note input stays for retry
    } finally {
      setAddingNote(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between pr-6">
            <DialogTitle>{data ? `${data.external_code} — ${data.head_name}` : 'Household detail'}</DialogTitle>
            {data && !editMode && (
              <Button variant="outline" size="sm" onClick={enterEditMode}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </div>
        </DialogHeader>

        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {saveError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {saveError}
          </div>
        )}

        {data && !loading && !editMode && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {[data.locality, data.village_neighborhood, data.site_name].filter(Boolean).join(', ') || '—'}
              </div>
              {data.assigned_chw && (
                <div className="col-span-2 flex items-center gap-2 rounded-md border p-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="font-medium">{data.assigned_chw.name || data.assigned_chw.chw_code}</div>
                    <div className="text-xs text-muted-foreground">{data.assigned_chw.aire_fosa}</div>
                  </div>
                  {data.assigned_chw.phone_number && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {data.assigned_chw.phone_number}
                    </div>
                  )}
                </div>
              )}
            </div>

            <section>
              <h4 className="mb-2 text-sm font-semibold">Composition</h4>
              <div className="grid grid-cols-3 gap-2 text-sm sm:grid-cols-4">
                <Stat label="Members" value={data.composition.household_members} />
                <Stat label="Under 5" value={data.composition.children_under_5} />
                <Stat label="Pregnant" value={data.composition.pregnant_women} />
                <Stat label="Elderly 60+" value={data.composition.elderly_60_plus} />
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-sm font-semibold">MCH summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground">ANC status</div>
                  <div>{data.mch.anc_status || '—'}</div>
                </div>
                <div className="rounded-md border p-2">
                  <div className="text-xs text-muted-foreground">Next ANC appointment</div>
                  <div>{data.mch.next_anc_appointment || '—'}</div>
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-sm font-semibold">Members ({data.members.length})</h4>
              <div className="space-y-1 text-sm">
                {data.members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <span>{m.full_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {m.age_years != null ? `${m.age_years}y` : '—'} · {m.role_in_hh}
                      {m.is_pregnant ? ' · pregnant' : ''}
                      {m.suspected_illness ? ' · suspected illness' : ''}
                      {m.confirmed_illness ? ' · confirmed illness' : ''}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h4 className="mb-2 text-sm font-semibold">Notes ({data.notes.length})</h4>
              {data.notes.length === 0 ? (
                <div className="mb-2 text-sm text-muted-foreground">No notes yet.</div>
              ) : (
                <div className="mb-2 space-y-2">
                  {data.notes.map((n) => (
                    <div key={n.id} className="rounded-md border p-2 text-sm">
                      <div>{n.body}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {n.recorded_by_name || 'Unknown'} · {new Date(n.recorded_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add a note…"
                  className="min-h-[38px] flex-1"
                />
                <Button size="sm" onClick={handleAddNote} disabled={!noteText.trim() || addingNote}>
                  {addingNote ? 'Adding…' : 'Add'}
                </Button>
              </div>
            </section>
          </div>
        )}

        {data && !loading && editMode && (
          <div className="space-y-6">
            <section className="space-y-3">
              <h4 className="text-sm font-semibold">Household</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Locality</Label>
                  <Input value={locality} onChange={(e) => setLocality(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Village / neighborhood</Label>
                  <Input value={villageNeighborhood} onChange={(e) => setVillageNeighborhood(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Site</Label>
                  <Input value={siteName} onChange={(e) => setSiteName(e.target.value)} />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input value={addressText} onChange={(e) => setAddressText(e.target.value)} />
                </div>
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Members</h4>
                <Button type="button" variant="outline" size="sm" onClick={addMemberDraft}>
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add member
                </Button>
              </div>

              {membersDraft.map((m, idx) => (
                <div key={m.id ?? `new-${idx}`} className="space-y-2 rounded-md border p-3">
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input
                        value={m.full_name}
                        onChange={(e) => updateMemberDraft(idx, { full_name: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Gender</Label>
                      <Select
                        value={m.sex === 'unknown' ? '' : m.sex}
                        onValueChange={(v) => updateMemberDraft(idx, { sex: v as 'male' | 'female' })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Age</Label>
                      <Input
                        type="number"
                        min={0}
                        value={m.age_years}
                        onChange={(e) => updateMemberDraft(idx, { age_years: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Role</Label>
                      <Select value={m.role_in_hh} onValueChange={(v) => updateMemberDraft(idx, { role_in_hh: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="head">Head</SelectItem>
                          <SelectItem value="spouse">Spouse</SelectItem>
                          <SelectItem value="child">Child</SelectItem>
                          <SelectItem value="member">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 space-y-1">
                      <Label className="text-xs">ID #</Label>
                      <Input
                        value={m.id_number}
                        onChange={(e) => updateMemberDraft(idx, { id_number: e.target.value })}
                      />
                    </div>
                    <div className="col-span-1 flex items-end justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeMemberDraft(idx)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <CheckField
                      label="Pregnant"
                      checked={m.is_pregnant}
                      onChange={(v) => updateMemberDraft(idx, { is_pregnant: v })}
                    />
                    <CheckField
                      label="Lactating"
                      checked={m.is_lactating}
                      onChange={(v) => updateMemberDraft(idx, { is_lactating: v })}
                    />
                    <CheckField
                      label="Attending ANC"
                      checked={m.attending_anc}
                      onChange={(v) => updateMemberDraft(idx, { attending_anc: v })}
                    />
                    <CheckField
                      label="Nutrition at risk"
                      checked={m.nutrition_at_risk}
                      onChange={(v) => updateMemberDraft(idx, { nutrition_at_risk: v })}
                    />
                    <CheckField
                      label="Danger signs"
                      checked={m.has_danger_signs}
                      onChange={(v) => updateMemberDraft(idx, { has_danger_signs: v })}
                    />
                    <CheckField
                      label="Suspected illness"
                      checked={m.suspected_illness}
                      onChange={(v) => updateMemberDraft(idx, { suspected_illness: v })}
                    />
                    <CheckField
                      label="Confirmed illness"
                      checked={m.confirmed_illness}
                      onChange={(v) => updateMemberDraft(idx, { confirmed_illness: v })}
                    />
                    <CheckField
                      label="Death reported"
                      checked={m.death_reported}
                      onChange={(v) => updateMemberDraft(idx, { death_reported: v })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {m.attending_anc && (
                      <div className="space-y-1">
                        <Label className="text-xs">Next ANC date</Label>
                        <Input
                          type="date"
                          value={m.next_anc_date}
                          onChange={(e) => updateMemberDraft(idx, { next_anc_date: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-xs">MUAC (cm)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={m.muac_cm}
                        onChange={(e) => updateMemberDraft(idx, { muac_cm: e.target.value })}
                      />
                    </div>
                    {(m.suspected_illness || m.confirmed_illness) && (
                      <div className="space-y-1">
                        <Label className="text-xs">Illness type</Label>
                        <Input
                          value={m.illness_type}
                          onChange={(e) => updateMemberDraft(idx, { illness_type: e.target.value })}
                        />
                      </div>
                    )}
                    {m.death_reported && (
                      <div className="space-y-1">
                        <Label className="text-xs">Date of death</Label>
                        <Input
                          type="date"
                          value={m.died_at}
                          onChange={(e) => updateMemberDraft(idx, { died_at: e.target.value })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </section>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                <X className="mr-1.5 h-3.5 w-3.5" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !membersValid}>
                <Check className="mr-1.5 h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-md border p-2 text-center">
    <div className="text-lg font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

const CheckField: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="flex items-center gap-2 text-sm">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
    {label}
  </label>
);

export default HouseholdDetailModal;
